import { useCallback, useRef, useState } from 'react';
import { MAX_VOLUME, MIN_VOLUME } from '../audio-player-constants';
import { clamp } from '../audio-player-utils';
import type { FeedEntry } from '~/services/layout';

type UseAudioPlayerStateParams = {
  skipSeconds: number;
};

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/** Holds core player state and exposes behavior-preserving action callbacks. */
export function useAudioPlayerState({
  skipSeconds,
}: UseAudioPlayerStateParams): {
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  entry: FeedEntry | null;
  setEntry: React.Dispatch<React.SetStateAction<FeedEntry | null>>;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isPlaying: boolean;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  isMuted: boolean;
  setIsMuted: React.Dispatch<React.SetStateAction<boolean>>;
  isQueueOpen: boolean;
  setIsQueueOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSpeedMenuOpen: boolean;
  setIsSpeedMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isFavorited: boolean;
  setIsFavorited: React.Dispatch<React.SetStateAction<boolean>>;
  isPlaylistAdded: boolean;
  setIsPlaylistAdded: React.Dispatch<React.SetStateAction<boolean>>;
  currentTime: number;
  setCurrentTime: React.Dispatch<React.SetStateAction<number>>;
  duration: number;
  setDuration: React.Dispatch<React.SetStateAction<number>>;
  volume: number;
  setVolume: React.Dispatch<React.SetStateAction<number>>;
  playbackRate: number;
  setPlaybackRate: React.Dispatch<React.SetStateAction<number>>;
  playbackSpeedOpen: boolean;
  setPlaybackSpeedOpen: React.Dispatch<React.SetStateAction<boolean>>;
  togglePlayback: () => void;
  closePlayer: () => void;
  handleSeek: (event: React.ChangeEvent<HTMLInputElement>) => void;
  formatTime: (seconds: number) => string;
  handleSkipBack: () => void;
  handleSkipAhead: () => void;
  handleVolumeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  toggleMute: () => void;
  selectPlaybackRate: (nextRate: number) => void;
} {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [entry, setEntry] = useState<FeedEntry | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isQueueOpen, setIsQueueOpen] = useState<boolean>(false);
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState<boolean>(false);
  const [isFavorited, setIsFavorited] = useState<boolean>(false);
  const [isPlaylistAdded, setIsPlaylistAdded] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(1);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [playbackSpeedOpen, setPlaybackSpeedOpen] = useState<boolean>(false);

  const togglePlayback = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const closePlayer = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.src = '';
      audioRef.current.load();
    }
    setEntry(null);
    setIsPlaying(false);
    setIsExpanded(false);
    setIsQueueOpen(false);
    setIsSpeedMenuOpen(false);
    setPlaybackSpeedOpen(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const handleSeek = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const time = Number(event.target.value);
      if (audioRef.current) audioRef.current.currentTime = time;
      setCurrentTime(time);
    },
    []
  );

  const handleSkipBack = useCallback(() => {
    if (!audioRef.current) return;

    const nextTime = Math.max(audioRef.current.currentTime - skipSeconds, 0);
    audioRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
  }, [skipSeconds]);

  const handleSkipAhead = useCallback(() => {
    if (!audioRef.current) return;

    const maxDuration = Number.isFinite(audioRef.current.duration)
      ? audioRef.current.duration
      : Number.POSITIVE_INFINITY;
    const nextTime = Math.min(
      audioRef.current.currentTime + skipSeconds,
      maxDuration
    );
    audioRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
  }, [skipSeconds]);

  const handleVolumeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextVolume = clamp(
        Number(event.target.value),
        MIN_VOLUME,
        MAX_VOLUME
      );
      setVolume(nextVolume);

      if (nextVolume === 0) {
        setIsMuted(true);
        return;
      }

      if (nextVolume > 0 && isMuted) setIsMuted(false);
    },
    [isMuted]
  );

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const selectPlaybackRate = useCallback((nextRate: number) => {
    setPlaybackRate(nextRate);
    setIsSpeedMenuOpen(false);
    setPlaybackSpeedOpen(false);
  }, []);

  return {
    audioRef,
    entry,
    setEntry,
    isExpanded,
    setIsExpanded,
    isPlaying,
    setIsPlaying,
    isMuted,
    setIsMuted,
    isQueueOpen,
    setIsQueueOpen,
    isSpeedMenuOpen,
    setIsSpeedMenuOpen,
    isFavorited,
    setIsFavorited,
    isPlaylistAdded,
    setIsPlaylistAdded,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    volume,
    setVolume,
    playbackRate,
    setPlaybackRate,
    playbackSpeedOpen,
    setPlaybackSpeedOpen,
    togglePlayback,
    closePlayer,
    handleSeek,
    formatTime,
    handleSkipBack,
    handleSkipAhead,
    handleVolumeChange,
    toggleMute,
    selectPlaybackRate,
  };
}
